import { Save } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { Api } from '@core/api';
import {
    ChatNotificationSelection,
    CommunityAccessLevel,
    CommunitySettingsSupportDto
} from '@core/api/ChallengeClient';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { useCommunitySettingsStore } from '@src/communities/community/details/store';

const accessLevelOptions = [
    { label: 'All', value: CommunityAccessLevel.ALL },
    { label: 'Managers', value: CommunityAccessLevel.MANAGERS },
    { label: 'None', value: CommunityAccessLevel.NONE }
];

const chatNotificationOptions = [
    { label: 'None', value: ChatNotificationSelection.NONE },
    {
        label: 'Mentions and Replies',
        value: ChatNotificationSelection.MENTIONS_AND_REPLIES
    },
    { label: 'All', value: ChatNotificationSelection.ALL }
];

type ToggleField = {
    [K in keyof CommunitySettingsSupportDto]-?: CommunitySettingsSupportDto[K] extends
        | boolean
        | undefined
        ? K
        : never;
}[keyof CommunitySettingsSupportDto];

const featureToggles: { field: ToggleField; label: string }[] = [
    { field: 'leaveEnabled', label: 'Leave' },
    { field: 'achievementsEnabled', label: 'Achievements' },
    { field: 'duelsEnabled', label: 'Duels' },
    { field: 'teamsEnabled', label: 'Teams' },
    { field: 'singleTeamMembershipEnforced', label: 'Single Team Membership' },
    { field: 'chatModerationEnabled', label: 'Chat Moderation' },
    {
        field: 'challengeSubscriptionDetailsVisible',
        label: 'Challenge Subscription Details Visible'
    },
    { field: 'fastrelayEnabled', label: 'Fastrelay Enabled' },
    { field: 'liveEventsEnabled', label: 'Live Events Enabled' }
];

const EditCommunitySettingsForm = observer(() => {
    const { communityId } = useParams();
    const navigate = useNavigate();
    const { challengeClient } = injectTSDI(Api);

    const { communitySettings } = useCommunitySettingsStore();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState<CommunitySettingsSupportDto>({
        eventCreation: CommunityAccessLevel.ALL,
        memberInvitation: CommunityAccessLevel.ALL,
        notificationsChat: ChatNotificationSelection.MENTIONS_AND_REPLIES,
        leaveEnabled: true,
        achievementsEnabled: true,
        duelsEnabled: true,
        teamsEnabled: true,
        teamCreation: CommunityAccessLevel.ALL,
        chatModerationEnabled: false,
        challengeSubscriptionDetailsVisible: false,
        feedEnabled: false,
        fastrelayEnabled: false,
        liveEventsEnabled: false,
        defaultTeamMemberLimit: undefined,
        singleTeamMembershipEnforced: false,
        feedPostCreation: CommunityAccessLevel.ALL,
        excludedActivityTypes: [],
        excludedWorkoutCategories: [],
        excludedWorkoutTypes: []
    });

    useEffect(() => {
        const fetchCommunitySettings = async () => {
            try {
                setLoading(true);
                const settingsData = await communitySettings.request([
                    parseInt(communityId)
                ]);
                if (settingsData) {
                    setFormData({
                        eventCreation: settingsData.eventCreation,
                        memberInvitation: settingsData.memberInvitation,
                        notificationsChat: settingsData.notificationsChat,
                        leaveEnabled: settingsData.leaveEnabled,
                        achievementsEnabled: settingsData.achievementsEnabled,
                        duelsEnabled: settingsData.duelsEnabled,
                        teamsEnabled: settingsData.teamsEnabled,
                        teamCreation: settingsData.teamCreation,
                        chatModerationEnabled:
                            settingsData.chatModerationEnabled ?? false,
                        challengeSubscriptionDetailsVisible:
                            settingsData.challengeSubscriptionDetailsVisible ??
                            false,
                        feedEnabled: settingsData.feedEnabled ?? false,
                        fastrelayEnabled:
                            settingsData.fastrelayEnabled ?? false,
                        liveEventsEnabled:
                            settingsData.liveEventsEnabled ?? false,
                        defaultTeamMemberLimit:
                            settingsData.defaultTeamMemberLimit,
                        singleTeamMembershipEnforced:
                            settingsData.singleTeamMembershipEnforced ?? false,
                        feedPostCreation:
                            settingsData.feedPostCreation ??
                            CommunityAccessLevel.ALL,
                        excludedActivityTypes:
                            settingsData.excludedActivityTypes ?? [],
                        excludedWorkoutCategories:
                            settingsData.excludedWorkoutCategories ?? [],
                        excludedWorkoutTypes:
                            settingsData.excludedWorkoutTypes ?? []
                    });
                }
            } catch (err: any) {
                console.error('Error fetching community settings:', err);
                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to fetch community settings'
                );
            } finally {
                setLoading(false);
            }
        };
        fetchCommunitySettings();
    }, [communityId, communitySettings]);

    const setField = <K extends keyof CommunitySettingsSupportDto>(
        field: K,
        value: CommunitySettingsSupportDto[K]
    ) => setFormData((prev) => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            const updateData: CommunitySettingsSupportDto = {
                eventCreation: formData.eventCreation,
                memberInvitation: formData.memberInvitation,
                notificationsChat: formData.notificationsChat,
                leaveEnabled: formData.leaveEnabled,
                achievementsEnabled: formData.achievementsEnabled,
                duelsEnabled: formData.duelsEnabled,
                teamsEnabled: formData.teamsEnabled,
                teamCreation: formData.teamCreation,
                chatModerationEnabled: formData.chatModerationEnabled,
                challengeSubscriptionDetailsVisible:
                    formData.challengeSubscriptionDetailsVisible,
                feedEnabled: true,
                fastrelayEnabled: formData.fastrelayEnabled,
                liveEventsEnabled: formData.liveEventsEnabled,
                defaultTeamMemberLimit:
                    formData.defaultTeamMemberLimit || undefined,
                singleTeamMembershipEnforced:
                    formData.singleTeamMembershipEnforced,
                feedPostCreation: formData.feedPostCreation,
                excludedActivityTypes: formData.excludedActivityTypes,
                excludedWorkoutCategories: formData.excludedWorkoutCategories,
                excludedWorkoutTypes: formData.excludedWorkoutTypes
            };

            await challengeClient.support.communitySettingsSupportControllerUpdateGeneralSettings(
                parseInt(communityId),
                updateData
            );

            navigate(links.communityDetailsSettings(communityId));
        } catch (err: any) {
            console.error('Error updating community settings:', err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to update community settings'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(links.communityDetailsSettings(communityId));
    };

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <>
            <PageHeader title={`Edit Community Settings: ${communityId}`} />

            <div className="flex max-w-[900px] flex-col gap-6 pb-24">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <FormSection
                    title="Permissions"
                    description="Who may create events, invite members, create teams and post to the feed"
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <Select
                            label="Event Creation"
                            value={formData.eventCreation}
                            options={accessLevelOptions}
                            onChange={(value) =>
                                setField('eventCreation', value)
                            }
                        />
                        <Select
                            label="Member Invitation"
                            value={formData.memberInvitation}
                            options={accessLevelOptions}
                            onChange={(value) =>
                                setField('memberInvitation', value)
                            }
                        />
                        <Select
                            label="Team Creation"
                            value={formData.teamCreation}
                            options={accessLevelOptions}
                            onChange={(value) =>
                                setField('teamCreation', value)
                            }
                        />
                        <Select
                            label="Feed Post Creation"
                            value={formData.feedPostCreation}
                            options={accessLevelOptions}
                            onChange={(value) =>
                                setField('feedPostCreation', value)
                            }
                        />
                        <Select
                            label="Chat Notifications"
                            value={formData.notificationsChat}
                            options={chatNotificationOptions}
                            onChange={(value) =>
                                setField('notificationsChat', value)
                            }
                        />
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Default Team Size Limit"
                                type="number"
                                value={formData.defaultTeamMemberLimit ?? ''}
                                onChange={(value) =>
                                    setField(
                                        'defaultTeamMemberLimit',
                                        value
                                            ? parseInt(String(value), 10)
                                            : undefined
                                    )
                                }
                            />
                            <p className="text-xs text-muted-foreground">
                                Leave empty for no limit
                            </p>
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Feature Toggles">
                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                        {featureToggles.map(({ field, label }) => (
                            <div
                                key={field}
                                className="flex items-center gap-2"
                            >
                                <Switch
                                    id={field}
                                    checked={Boolean(formData[field])}
                                    onCheckedChange={(checked) =>
                                        setField(field, checked)
                                    }
                                />
                                <Label htmlFor={field}>{label}</Label>
                            </div>
                        ))}
                    </div>
                </FormSection>
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button onClick={handleSave} loading={saving}>
                    <Save />
                    Save
                </Button>
            </StickyBar>
        </>
    );
});

export default EditCommunitySettingsForm;
